import { GCModuleEnum } from '@business/constants/permissions';
import { UserId, UserRole } from '@business/entities/account';
import { ChecklistTemplateResponse, ChecklistId, SortByOptions, SortByDirections } from '@business/entities/checklist';
import { TeamId, TeamResponse } from '@business/entities/team';
import { CommonService, TaskService } from '@business/services';
import { FormSelect } from '@core/components/form';
import { useHistory, useRouteParam, useQueryStringParam } from '@core/router';
import { useBloc } from '@core/utils/bloc';
import { formControl, formGroup } from '@core/utils/form';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { R } from '@core/utils/r';
import { toMoment } from '@core/utils/time';
import { Session } from '@modules/auth/session';
// import './index.scss';
import { HeaderButtons, PageHeader, Tabs, TabsMapping, TabsHeader, TabHeaderItem } from '@modules/common';
import { openTodoChecklistOverlay } from '@modules/dashboard/DashboardPage/Components/TodoChecklistOverlay';
import { TaskList } from '@modules/dashboard/DashboardPage/TasksMenu/TaskList';
import { openTaskOverlay } from '@modules/tasks/TaskDetailModal';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Container, UncontrolledTooltip } from 'reactstrap';
import { TodosPageBloc } from './TodosPageBloc';
import { SortEnd } from 'react-sortable-hoc';
import { AppConfig } from '@core/utils/config';
import { Endpoint } from '@business/api/endpoint';
import { CalendarTaskList } from '@modules/dashboard/DashboardPage/TasksMenu/CalendarTaskList';
import { SystemCalendar } from '@modules/common/SystemCalendar';

function defineUserTeamForm() {
  return formGroup({
    id: formControl<UserId | TeamId>({
      placeholder: 'Select User',
    }),
    sortBy: formControl<SortByOptions>({
      placeholder: 'Sort By',
    }),
    sortByDirection: formControl<SortByDirections>({
      placeholder: 'Direction',
    }),
  });
}

export const TodosPage = memo(() => {
  const appId = useRouteParam('appId', true);

  useEffect(() => {
    if (!R.isEmpty(appId)) {
      AppConfig.set({ appId });
      Endpoint.setAppId(appId);
    }
  }, [appId]);

  const checklistId = useRouteParam('checklistId', true);
  const bloc = useBloc(TodosPageBloc, checklistId);
  const tasks = useObservable(bloc.tasks$, []);
  const checklists = useObservable(bloc.checklists$, []);
  const users = useObservable(bloc.allUsers$, []);
  const teams = useObservable(bloc.teams$, []);
  const form = useMemo(() => defineUserTeamForm(), []);
  const formValue = useNonNilObservable(form.value$);
  const history = useHistory();
  const [uid, setUid] = useState('');
  const taskId = useRouteParam('taskId', true);
  const [cTaskId, setCTaskId] = useState('');
  const user = useObservable(Session.user$);
  const permission = R.find(user?.permissions || [], p => p.module === GCModuleEnum.todo);
  const permissionIds = permission?.ids || [];
  const taskStatuses = useObservable(bloc.taskStatuses$, []);
  const [currentTab, setCurrentTab] = useState(0);
  const [isCalendar, setCalendar] = useState(false);
  const isMe = useMemo(() => permissionIds.includes('-1'), [permissionIds]);
  const [isChecklistOpen, setChecklistOpen] = useState(false);
  // `?view=systemCalendar` opens straight into the system calendar — used by the "open in a new tab"
  // calendar icon on the work-order overlay.
  const initialView = useQueryStringParam('view');
  const [isSystemCalendar, setSystemCalendar] = useState(initialView === 'systemCalendar');

  // const myTeams = useMemo(
  //   () =>
  //     R.filter(teams, t => {
  //       return R.find(t.users, u => u.id === user?.id);
  //     }),
  //   [teams, user],
  // ) as TeamResponse[];
  // const teamsToUse = useMemo(() => (isMe ? myTeams : teams), [isMe, myTeams, teams]);

  const userTeamItems = useMemo(
    () =>
      CommonService.selectGroupedItems(
        [
          { label: 'All Users', items: !isMe ? [{ label: 'All', value: `all` }] : [] },
          {
            label: 'Users',
            items: R.filter(
              users,
              u =>
                (R.includes(permissionIds, 'all') ||
                  R.intersection(permissionIds, u.teams).length > 0 ||
                  u.id === user?.id ||
                  user?.role === UserRole.admin) &&
                (u.role === UserRole.admin || u.role === UserRole.user),
            ).map(i => ({ label: CommonService.getFullName(i), value: `user-${i.id}` })),
          },
          { label: 'Teams', items: !isMe ? teams.map(i => ({ label: i.name, value: `team-${i.id}` })) : [] },
        ],
        'label',
        s => s.items,
        'label',
        'value',
      ),
    [isMe, users, teams, permissionIds, user],
  );

  const onClose = useCallback(() => {
    setCTaskId('');
    setChecklistOpen(false);
    history.push(`/todos/`);
    if (uid && !R.isEmpty(uid)) {
      bloc.search(uid).subscribe();
      bloc.searchTask(uid).subscribe();
    } else {
      bloc.onTodoReload();
    }
  }, [bloc, history, uid]);

  const onSortEnd = useCallback(
    (sort: SortEnd) => {
      const newArray = R.move(tasks, sort.oldIndex, sort.newIndex);
      const sortedIds = newArray.map((item, index) => ({ id: item.id, menuOrder: index }));
      bloc.orderTasks(sortedIds).subscribe();
    },
    [bloc, tasks],
  );

  useEffect(() => {
    if (taskId && !R.isEmpty(taskId.toString()) && cTaskId !== taskId) {
      setCTaskId(taskId);
      openTaskOverlay({ taskId: taskId, onClose, assignee: [formValue.id], onSortEnd });
    }
  }, [bloc, cTaskId, formValue.id, onClose, onSortEnd, taskId]);

  useEffect(() => {
    if (checklistId && !R.isEmpty(checklistId.toString()) && !isChecklistOpen) {
      setChecklistOpen(true);
      openTodoChecklistOverlay({ checklistId: checklistId, onClose });
    }
  }, [bloc, checklistId, isChecklistOpen, onClose]);

  useEffect(() => {
    if (user) {
      form.patchValue({
        id: `user-${user.id}`,
      });
    }
  }, [form, user]);

  const taskCounts = useMemo(() => {
    const now = toMoment().unix();
    const overdue = tasks.filter(task => {
      return task.dueDate && task.dueDate < now && task.isCompleted === false;
    }).length;
    const pending = tasks.filter(task => {
      return task.isCompleted === false;
    }).length;
    const completed = tasks.filter(task => {
      return task.isCompleted;
    }).length;

    return { overdue, pending, completed };
  }, [tasks]);

  const checklistCount = useMemo(() => {
    const pending = checklists.filter(c => {
      return c.isCompleted === false;
    }).length;
    const completed = checklists.filter(c => {
      return c.isCompleted;
    }).length;

    return { pending, completed };
  }, [checklists]);

  const completedCount = useMemo(() => {
    return taskCounts.completed + checklistCount.completed;
  }, [taskCounts.completed, checklistCount.completed]);

  const pendingCount = useMemo(() => {
    return taskCounts.pending + checklistCount.pending;
  }, [taskCounts.pending, checklistCount.pending]);

  const searchItems = useCallback(
    (id: UserId) => {
      if (!id && user) {
        id = `user-${user.id}`;
        form.patchValue({ id });
      }
      setUid(id);
      bloc.search(id).subscribe();
      bloc.searchTask(id).subscribe();
    },
    [bloc, form, user],
  );

  const onCreate = useCallback(
    (id: ChecklistTemplateResponse, isChecklist: boolean) => {
      if (isChecklist && !R.isEmpty(uid.toString())) {
        bloc.search(uid).subscribe();
      }
    },
    [bloc, uid],
  );

  const onAssign = useCallback(
    (type: string, checklistId: ChecklistId, assignees: string[]) => {
      return bloc.updateAssignne(type, checklistId, assignees || []).subscribe();
    },
    [bloc],
  );

  const tabs: TabHeaderItem[] = useMemo(
    () => [
      {
        title: 'Your Tasks',
        rightHTML: (
          <>
            {pendingCount > 0 && <span className="text-dark ml-1"> {pendingCount} </span>}
            {taskCounts.overdue > 0 && <span className="text-danger ml-1">({taskCounts.overdue})</span>}
          </>
        ),

        route: 'pending',
      },
      {
        title: 'Completed',
        rightHTML: <>{completedCount > 0 && <span className="text-dark ml-1"> {completedCount}</span>}</>,

        route: 'completed',
      },
    ],
    [pendingCount, taskCounts.overdue, completedCount],
  );

  const buttons: HeaderButtons[] = useMemo(() => {
    return [
      {
        children: (
          <div key="btn0" style={{ width: 200, marginRight: 10 }}>
            <FormSelect isClearable control={form.controls.id} items={userTeamItems} onChange={id => searchItems(id)} />
          </div>
        ),
      },
      {
        children: (
          <div key="btn1" style={{ width: 200, marginRight: 10 }}>
            <FormSelect
              isClearable
              control={form.controls.sortBy}
              onChange={() => form.patchValue({ sortByDirection: SortByDirections.asc })}
              items={TaskService.getAllSortByItems()}
            />
          </div>
        ),
      },
      {
        children: (
          <div key="btn2" style={{ width: 200, marginRight: 10 }}>
            <FormSelect isClearable control={form.controls.sortByDirection} items={TaskService.getAllSortByDirectionItems()} />
          </div>
        ),
      },
    ];
  }, [form, searchItems, userTeamItems]);

  const onRoute = useCallback(
    (tab: string) => {
      if (checklistId) {
        return `/todos/${tab}/checklist/${checklistId}`;
      }
      return `/todos/${tab}`;
    },
    [checklistId],
  );

  const handleClick = type => {
    switch (type) {
      case 'showSystemCalendar':
        setSystemCalendar(true);
        setCalendar(false);
        break;
      case 'showCalendar':
        setSystemCalendar(false);
        setCalendar(true);
        break;
      case 'hideBoth':
        setSystemCalendar(false);
        setCalendar(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <PageHeader buttons={buttons}>Tasks</PageHeader>
        <div id="task-menu-page" className="board-list-task" style={{ margin: 0 }}>
          <div className="main_tabs">
            <TabsHeader
              pills
              tabs={tabs}
              route={tab => onRoute(tab)}
              routeParam="type"
              onChange={tab => setCurrentTab(tab)}
              right={
                <div>
                  <div className="d-flex" key="view1">
                  <span className="d-flex hover-pointer ml-3" id="grid_view_new" onClick={() => handleClick('showSystemCalendar')}>
                      <i className={`fal fa-calendar-alt font-size-20 ${isSystemCalendar ? 'text-primary' : 'text-dark'}`}></i>
                      <UncontrolledTooltip placement="top" target={'grid_view_new'}>
                        System Calendar View
                      </UncontrolledTooltip>
                    </span>
                    <span className="d-flex hover-pointer ml-3" id="grid_view" onClick={() => handleClick('showCalendar')}>
                      <i className={`fal fa-calendar-alt font-size-20 ${isCalendar ? 'text-primary' : 'text-dark'}`}></i>
                      <UncontrolledTooltip placement="top" target={'grid_view'}>
                        Calendar View
                      </UncontrolledTooltip>
                    </span>
                    <span className="d-flex hover-pointer ml-3" id="list_view" onClick={() =>  handleClick('hideBoth')}>
                      <i className={`fal fa-th-list font-size-20 ${!isCalendar ? 'text-primary' : 'text-dark'}`}></i>
                      <UncontrolledTooltip placement="top" target={'list_view'}>
                        List View
                      </UncontrolledTooltip>
                    </span>
                  </div>
                </div>
              }
            />
            {!isCalendar && !isSystemCalendar && currentTab === 0 && (
              <TaskList
                bloc={bloc as any}
                isCompleted={false}
                assignee={[formValue.id]}
                onCreate={onCreate}
                onAssign={onAssign}
                parentSectionId="task-menu-page"
                sortBy={formValue.sortBy}
                sortByDirection={formValue.sortByDirection}
                taskStatuses={taskStatuses}
              />
            )}
            {!isCalendar && !isSystemCalendar && currentTab === 1 && (
              <TaskList
                bloc={bloc as any}
                isCompleted={true}
                onAssign={onAssign}
                parentSectionId="task-menu-page"
                sortBy={formValue.sortBy}
                sortByDirection={formValue.sortByDirection}
                taskStatuses={taskStatuses}
              />
            )}
            {isCalendar && <CalendarTaskList allTasks={tasks} bloc={bloc} isCompleted={currentTab === 1} />}
            {isSystemCalendar && <SystemCalendar /> }
            {/* <Tabs pills tabs={tabs} route={tab => `/todos/${tab}`} routeParam="type" /> */}
          </div>
        </div>
      </Container>
    </div>
  );
});
